/**
 * Home Dashboard widgets bootstrap.
 *
 * Three independent cards on page-home.php:
 *
 *  1. Risk Register stacked chart (Open & Under Review) — canvas
 *     #riskRegisterChartActive, payload window.riskChartData.
 *
 *  2. Risk Register by business unit — canvas #homeRiskChartBusinessUnit,
 *     payload window.riskBusinessUnitChartData.
 *
 *  3. Asset Register stacked chart (assets by type × criticality tier) —
 *     canvas #assetRegisterChartActive, payload window.assetChartData.
 *     Full-width row below the two risk widgets.
 *
 *  4. Project Dashboard widget — Project selection drives the "Maturity
 *     Ratings By Safeguard Domain" card.
 *
 * For #4 we DO NOT reuse js/assessments/dist/dashboard-bundle.min.js even
 * though that bundle contains every CRF-S year. The dashboard bundle also
 * ships web-assessment-charts-nist2.js, whose $(document).ready reassigns
 * WebAssessmentCharts.fetchDataAndCalculateScores to a NIST-specific
 * function — so on /home, every project pick would chase the NIST pipeline
 * against a #nist2 table that doesn't exist. Instead we ship a dedicated
 * js/assessments/dist/home-dashboard-bundle.min.js (built by the
 * `bundleHomeDashboard` gulp task) that includes CRF-S year scripts,
 * NIST/CIS/NYCRR chart modules (via home-dashboard-registry.js), and
 * js/home-dashboard/home-dashboard-dispatch.js, which publishes:
 *
 *   - WebAssessmentCharts.homeDashboardFetchForProject(slug, nativeFrameworkSlug, viewFrameworkSlug)
 *     → runs fetchDataAndCalculateScores{NATIVE}; when the dashboard view year (or 2026 sub-version)
 *       differs, applies remapProcessedItemsForTargetYear + target quality JSON like view-results CRFS.
 *       Resolves to { year: viewYear, frameworkSlug } for chart render.
 *   - WebAssessmentCharts.homeDashboardRenderTechnicalChart(year, frameworkSlug)
 *     → shows the right CRF container and calls createChartFromTable.
 *   - WebAssessmentCharts.homeDashboardActivateFrameworkPanels(frameworkSlug)
 *     → shows the NIST/CIS/NYCRR panel that the fetcher populated.
 *
 * The dispatcher also detaches the .project-selection / .dashboard-selection
 * change handlers each year-file's $(document).ready attached. This file is
 * therefore the SOLE driver of those two selects on the home page.
 */
(function ($) {
    'use strict';

    var LOG_PREFIX = '[home-dashboard]';
    var HOME_DASHBOARD_LOADER_MAX_MS = 15000;
    var homeDashboardReadyState = {
        charts: false,
        projectDashboard: false
    };

    function tryRevealHomeDashboardWidgets() {
        if (!$('.home-dashboard-widgets').length) {
            return;
        }
        if (!homeDashboardReadyState.charts || !homeDashboardReadyState.projectDashboard) {
            return;
        }
        log('Home page ready — revealing content.');
        if (typeof window.crfRevealHomePage === 'function') {
            window.crfRevealHomePage();
        } else {
            $('#home-page-loader').hide();
            $('#main-content').removeClass('d-none').addClass('d-block');
            $('body').addClass('loaded');
        }
    }

    function markHomeDashboardChartsReady() {
        function done() {
            homeDashboardReadyState.charts = true;
            tryRevealHomeDashboardWidgets();
        }
        if (typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(function () {
                window.requestAnimationFrame(done);
            });
        } else {
            window.setTimeout(done, 50);
        }
    }

    function markHomeDashboardProjectReady() {
        homeDashboardReadyState.projectDashboard = true;
        tryRevealHomeDashboardWidgets();
    }

    window.crfMarkHomeDashboardProjectReady = markHomeDashboardProjectReady;

    function log() {
        if (typeof console === 'undefined') return;
        var args = Array.prototype.slice.call(arguments);
        args.unshift(LOG_PREFIX);
        console.log.apply(console, args);
    }
    function warn() {
        if (typeof console === 'undefined') return;
        var args = Array.prototype.slice.call(arguments);
        args.unshift(LOG_PREFIX);
        console.warn.apply(console, args);
    }

    // -- Risk chart ----------------------------------------------------------

    var RISK_STATUS_ACTIVE = ['Open', 'Under Review'];
    var RISK_RATING_ORDER = ['Low', 'Moderate', 'High', 'Critical'];
    /** Same threat-rating palette as js/risk-register/risk-register.js. */
    var RISK_RATING_COLORS = {
        'Low':      'rgba(22, 163, 74, 0.9)',
        'Moderate': 'rgba(217, 119, 6, 0.9)',
        'High':     'rgba(234, 88, 12, 0.9)',
        'Critical': 'rgba(220, 38, 38, 0.9)'
    };

    /**
     * Sync empty-state overlay on a home register chart card (server sets data-chart-has-data).
     */
    function syncChartEmptyOverlay(canvasId) {
        var canvas = document.getElementById(canvasId);
        if (!canvas) return;
        var $wrapper = $(canvas).closest('[data-chart-has-data]');
        if (!$wrapper.length) return;
        var hasData = $wrapper.attr('data-chart-has-data') === '1';
        var $overlay = $wrapper.find('.home-chart-empty-overlay');
        if (!$overlay.length) return;
        if (hasData) {
            $overlay.hide();
            $wrapper.removeClass('is-chart-empty');
        } else {
            $overlay.css('display', 'flex');
            $wrapper.addClass('is-chart-empty');
        }
    }

    function buildRiskDatasets(data, statusList) {
        var labels = statusList.filter(function (s) { return data[s] !== undefined; });
        if (labels.length === 0) labels = statusList.slice();
        var datasets = RISK_RATING_ORDER.map(function (rating) {
            var values = labels.map(function (status) {
                var row = data[status];
                return (row && row[rating] !== undefined) ? row[rating] : 0;
            });
            return {
                label: rating,
                data: values,
                backgroundColor: RISK_RATING_COLORS[rating] || 'rgba(128, 128, 128, 0.9)',
                stack: 'stack0'
            };
        });
        return { labels: labels, datasets: datasets };
    }

    function renderRiskChart() {
        if (typeof Chart === 'undefined') { warn('Chart.js not loaded.'); return; }
        var canvas = document.getElementById('riskRegisterChartActive');
        if (!canvas) return;
        syncChartEmptyOverlay('riskRegisterChartActive');
        var data = window.riskChartData;
        if (!data || typeof data !== 'object') { warn('riskChartData missing.', data); return; }
        log('Rendering risk chart.');
        var payload = buildRiskDatasets(data, RISK_STATUS_ACTIVE);
        new Chart(canvas.getContext('2d'), {
            type: 'bar',
            data: { labels: payload.labels, datasets: payload.datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: { padding: { bottom: 4 } },
                scales: {
                    x: { stacked: true, offset: true, grid: { offset: true } },
                    y: { stacked: true, beginAtZero: true, ticks: { stepSize: 1 }, title: { display: true, text: 'Number of risks' } }
                },
                plugins: {
                    legend: { display: true, position: 'right', labels: {
                        sort: function (a, b) {
                            var order = { 'Critical': 0, 'High': 1, 'Moderate': 2, 'Low': 3 };
                            return (order[a.text] != null ? order[a.text] : 4) - (order[b.text] != null ? order[b.text] : 4);
                        }
                    }},
                    tooltip: { callbacks: { label: function (ctx) {
                        var n = ctx.parsed.y;
                        return (ctx.dataset.label || '') + ': ' + n + ' risk' + (n !== 1 ? 's' : '');
                    }}}
                }
            }
        });
    }

    // -- Risk business unit chart --------------------------------------------

    var RISK_BUSINESS_UNIT_ORDER = [
        'Finance & Accounting',
        'Human Resources (HR)',
        'Legal & Compliance',
        'Sales & Business Development',
        'Marketing & Communications',
        'Technology & Cybersecurity',
        'Facilities & Physical Security',
        'Other'
    ];

    function buildRiskBusinessUnitDatasets(data) {
        if (!data || typeof data !== 'object') return { labels: [], datasets: [] };
        var labels = RISK_BUSINESS_UNIT_ORDER.filter(function (bu) { return data[bu] !== undefined; });
        Object.keys(data).forEach(function (bu) {
            if (labels.indexOf(bu) === -1) labels.push(bu);
        });
        if (labels.length === 0) labels = RISK_BUSINESS_UNIT_ORDER.slice();
        var datasets = RISK_RATING_ORDER.map(function (rating) {
            var values = labels.map(function (bu) {
                var row = data[bu];
                return (row && row[rating] !== undefined) ? row[rating] : 0;
            });
            return {
                label: rating,
                data: values,
                backgroundColor: RISK_RATING_COLORS[rating] || 'rgba(128, 128, 128, 0.9)',
                stack: 'stack0'
            };
        });
        return { labels: labels, datasets: datasets };
    }

    function renderRiskBusinessUnitChart() {
        if (typeof Chart === 'undefined') { warn('Chart.js not loaded.'); return; }
        var canvas = document.getElementById('homeRiskChartBusinessUnit');
        if (!canvas) return;
        syncChartEmptyOverlay('homeRiskChartBusinessUnit');
        var data = window.riskBusinessUnitChartData;
        if (!data || typeof data !== 'object') { warn('riskBusinessUnitChartData missing.', data); return; }
        log('Rendering risk business unit chart.');
        var payload = buildRiskBusinessUnitDatasets(data);
        new Chart(canvas.getContext('2d'), {
            type: 'bar',
            data: { labels: payload.labels, datasets: payload.datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: { padding: { bottom: 4 } },
                scales: {
                    x: {
                        stacked: true,
                        offset: true,
                        grid: { offset: true },
                        ticks: {
                            maxRotation: 45,
                            minRotation: 45,
                            autoSkip: false,
                            padding: 4
                        }
                    },
                    y: {
                        stacked: true,
                        beginAtZero: true,
                        ticks: { stepSize: 1 },
                        title: { display: true, text: 'Number of risks' }
                    }
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'right',
                        labels: {
                            sort: function (a, b) {
                                var order = { 'Critical': 0, 'High': 1, 'Moderate': 2, 'Low': 3 };
                                return (order[a.text] != null ? order[a.text] : 4) - (order[b.text] != null ? order[b.text] : 4);
                            }
                        }
                    },
                    tooltip: {
                        callbacks: {
                            label: function (ctx) {
                                var n = ctx.parsed.y;
                                return (ctx.dataset.label || '') + ': ' + n + ' risk' + (n !== 1 ? 's' : '');
                            }
                        }
                    }
                }
            }
        });
    }

    // -- Asset chart ---------------------------------------------------------

    var ASSET_TIER_ORDER = [
        'Tier 1 – Critical',
        'Tier 2 – Essential',
        'Tier 3 – Important',
        'Tier 4 – Supporting',
        'Tier 5 – Non-Critical'
    ];
    var ASSET_TIER_COLORS = {
        'Tier 1 – Critical':    'rgba(220, 38, 38, 0.9)',
        'Tier 2 – Essential':   'rgba(234, 88, 12, 0.9)',
        'Tier 3 – Important':   'rgba(217, 119, 6, 0.9)',
        'Tier 4 – Supporting':  '#5789bb',
        'Tier 5 – Non-Critical':'#314d76'
    };

    function buildAssetDatasets(data) {
        if (!data || typeof data !== 'object') return { labels: [], datasets: [] };
        var labels = Object.keys(data);
        var datasets = ASSET_TIER_ORDER.map(function (tier) {
            var values = labels.map(function (assetType) {
                var row = data[assetType];
                return (row && row[tier] !== undefined) ? row[tier] : 0;
            });
            return {
                label: tier,
                data: values,
                backgroundColor: ASSET_TIER_COLORS[tier] || 'rgba(128,128,128,0.9)',
                stack: 'stack0'
            };
        });
        return { labels: labels, datasets: datasets };
    }

    function renderAssetChart() {
        if (typeof Chart === 'undefined') return;
        var canvas = document.getElementById('assetRegisterChartActive');
        if (!canvas) return;
        syncChartEmptyOverlay('assetRegisterChartActive');
        var data = window.assetChartData;
        if (!data || typeof data !== 'object') { warn('assetChartData missing.', data); return; }
        log('Rendering asset chart.');
        var payload = buildAssetDatasets(data);
        new Chart(canvas.getContext('2d'), {
            type: 'bar',
            data: { labels: payload.labels, datasets: payload.datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: { padding: { bottom: 4 } },
                scales: {
                    x: {
                        stacked: true,
                        offset: true,
                        grid: { offset: true },
                        ticks: {
                            maxRotation: 45,
                            minRotation: 45,
                            autoSkip: false,
                            padding: 4
                        }
                    },
                    y: {
                        stacked: true,
                        beginAtZero: true,
                        ticks: { stepSize: 1 },
                        title: { display: true, text: 'Number of assets' }
                    }
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'right',
                        labels: {
                            sort: function (a, b) {
                                var ia = ASSET_TIER_ORDER.indexOf(a.text);
                                var ib = ASSET_TIER_ORDER.indexOf(b.text);
                                return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
                            }
                        }
                    },
                    tooltip: { callbacks: { label: function (ctx) {
                        var n = ctx.parsed.y;
                        return (ctx.dataset.label || '') + ': ' + n + ' asset' + (n !== 1 ? 's' : '');
                    }}}
                }
            }
        });
    }

    /**
     * True when the project's framework taxonomy slug is any CRF-S variant
     * (core year or 2026 sub-version).
     */
    function isCrfsFrameworkSlug(slug) {
        var s = String(slug || '').toLowerCase();
        if (!s) return false;
        return s === 'crf-s' || s.indexOf('crf-s-') === 0;
    }

    function homeDashboardHasRegisteredFetcher(frameworkSlug) {
        var fw = String(frameworkSlug || '').toLowerCase();
        if (!fw) return false;
        return !!(window.WebAssessmentCharts &&
            typeof WebAssessmentCharts.homeDashboardHasFetcher === 'function' &&
            WebAssessmentCharts.homeDashboardHasFetcher(fw));
    }

    /** Tooltip when the dashboard dropdown is locked (any project selected). */
    var HOME_DASHBOARD_SELECT_LOCKED_HINT =
        'The dashboard version is set from the project you select; use View results on the project to compare across frameworks or years.';

    var HOME_DASHBOARD_SELECT_NO_PROJECT_HINT = 'Select a project to load maturity charts.';

    /**
     * Enable or disable the dashboard <select> (and dim/block the Select2 UI).
     */
    function setHomeDashboardDashboardSelectEnabled($select, enabled, titleWhenDisabled) {
        if (!$select || !$select.length) return;
        var dis = !enabled;
        $select.prop('disabled', dis);
        if (dis && titleWhenDisabled) {
            $select.attr('title', titleWhenDisabled);
        } else {
            $select.removeAttr('title');
        }
        var $s2 = $select.next('.select2-container');
        if ($s2.length) {
            $s2.css('pointer-events', enabled ? '' : 'none');
            $s2.css('opacity', enabled ? '' : '0.65');
            if (dis && titleWhenDisabled) {
                $s2.attr('title', titleWhenDisabled);
            } else {
                $s2.removeAttr('title');
            }
        }
    }

    /**
     * Dashboard &lt;select&gt; is always locked on /home once a project is chosen
     * (CRF-S and third-party): the row stays synced to the project's framework
     * / standard; users change projects to change the view.
     * No project: disabled with a prompt to pick a project.
     */
    function hideHomeProjectDashboardPlaceholders($widget) {
        if (!$widget || !$widget.length) {
            return;
        }
        $widget.find('.select-a-project, .select-a-dashboard').hide();
    }

    function syncHomeDashboardSelectEnabledState($widget) {
        var $dashboardSel = $widget.find('.dashboard-selection');
        if (!$dashboardSel.length) return;
        var $projectSel = $widget.find('.project-selection');
        var slug = String($projectSel.val() || '').trim();
        if (!slug) {
            setHomeDashboardDashboardSelectEnabled(
                $dashboardSel,
                false,
                HOME_DASHBOARD_SELECT_NO_PROJECT_HINT
            );
            return;
        }
        setHomeDashboardDashboardSelectEnabled(
            $dashboardSel,
            false,
            HOME_DASHBOARD_SELECT_LOCKED_HINT
        );
    }
    // 'change.select2' on the dashboard select so the handler below knows the
    // event is a programmatic sync, not a real user pick. jQuery fires both
    // namespaced and non-namespaced handlers for a namespaced .trigger(), so
    // 'change.select2' WOULD otherwise re-fire our plain-'change' dashboard
    // handler and start a second dispatch against whatever year happens to
    // be auto-selected — which is exactly the year we just dispatched for.
    var suppressDashboardChange = false;

    /**
     * Pick the .dashboard-selection option whose data-standardid CSV contains
     * the project's primary standardid (see widget-project-dashboard.php for
     * how that attribute is set). Updates the visible Select2 tag only — the
     * dispatcher already knows which year to run, so we don't want a real
     * 'change' to ripple through.
     */
    function updateDashboardSelectionDisplay($widget, primaryStandardId) {
        var $dashboardSel = $widget.find('.dashboard-selection');
        if (!$dashboardSel.length || !primaryStandardId) return;
        var sid = String(primaryStandardId).toUpperCase();
        var targetIndex = -1;
        $dashboardSel.find('option').each(function (index) {
            var sids = ($(this).attr('data-standardid') || '').toUpperCase()
                .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
            if (sids.indexOf(sid) !== -1) {
                targetIndex = index;
                return false;
            }
        });
        if (targetIndex < 0) {
            warn('No .dashboard-selection option matches', sid);
            return;
        }
        var $opt = $dashboardSel.find('option').eq(targetIndex);

        var wasDisabled = !!$dashboardSel.prop('disabled');
        if (wasDisabled) {
            setHomeDashboardDashboardSelectEnabled($dashboardSel, true);
        }

        suppressDashboardChange = true;
        try {
            $dashboardSel.val($opt.val());
            if ($dashboardSel[0]) {
                $dashboardSel[0].selectedIndex = targetIndex;
            }
            // Select2 listens for 'change.select2' to redraw its rendered tag.
            // jQuery's namespaced trigger also fires the plain 'change'
            // handler we attached below; the suppressDashboardChange guard
            // keeps that handler from running runDispatch a second time.
            $dashboardSel.trigger('change.select2');
        } finally {
            suppressDashboardChange = false;
        }

        if (wasDisabled) {
            syncHomeDashboardSelectEnabledState($widget);
        }

        log('Synced dashboard dropdown to', $opt.text());
    }

    /**
     * Map a .dashboard-selection option to the framework slug
     * fetchDataAndCalculateScores{YYYY} expects. The option's
     * data-standardid attribute is the source of truth — for 2026 it's a
     * CSV like "CRF-S-CORE-2026,CRF-S-APPSEC-2026,…" so we pick the first
     * entry.
     */
    function dashboardOptionToFrameworkSlug($opt) {
        if (!$opt || !$opt.length) return null;
        var sids = ($opt.attr('data-standardid') || '').toUpperCase()
            .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        var first = sids[0] || '';
        if (first === 'CRF-S-2021') return 'crf-s-2021';
        if (first === 'CRF-S-2022') return 'crf-s-2022';
        if (first === 'CRF-S-2023') return 'crf-s-2023';
        if (first === 'CRF-S-2024') return 'crf-s-2024';
        if (first === 'CRF-S-2025') return 'crf-s-2025';
        if (first === 'CRF-S-CORE-2026') return 'crf-s-2026';
        if (first.indexOf('CRF-S-APPSEC-2026') === 0) return 'crf-s-appsec-2026';
        if (first.indexOf('CRF-S-GOV-2026') === 0) return 'crf-s-gov-2026';
        if (first.indexOf('CRF-S-HYG-2026') === 0) return 'crf-s-hyg-2026';
        if (first.indexOf('CRF-S-SMB-2026') === 0) return 'crf-s-smb-2026';

        var thirdPartyByStandardId = {
            'NIST-CSF-1-0': 'nist-csf-1-0',
            'NIST-CSF-1-1': 'nist-csf-1-1',
            'NIST-CSF-2-0': 'nist-csf-2-0',
            'CIS-5-0': 'cis-5-0',
            'CIS-6-0': 'cis-6-0',
            'CIS-7-0': 'cis-7-0',
            'CIS-7-1': 'cis-7-1',
            'CIS-8-0': 'cis-8-0',
            'CIS-8-1': 'cis-8-1',
            'NYCRR-500-2-0': 'nycrr-500-v2',
            'ISO-27002-2013': 'iso-27002-2013',
            'ISO-27002-2022': 'iso-27002-2022',
            'FCA-RM-2024': 'fca-rm-2024',
            'UC-POLICY-IS-3-2019': 'uc-policy-is-3',
            'NIST-SP-800-171-4-0': 'nist-800-171-rev3',
            'PCI-DSS-4-0': 'pci-dss-4-0',
            'SA-NCA-ECC-1-0': 'sa-nca-ecc-1-0',
            'SAMA-CRFR-1-0': 'sama-crfr-1-0'
        };
        return thirdPartyByStandardId[first] || null;
    }

    function initProjectDashboardWidget() {
        var $widget = $('.home-widget-project-dashboard');
        if (!$widget.length) {
            markHomeDashboardProjectReady();
            return;
        }

        if ($widget.attr('data-has-projects') === '0') {
            log('Assessment dashboard widget: no projects — showing empty state only.');
            markHomeDashboardProjectReady();
            return;
        }

        var $projectSel      = $widget.find('.project-selection');
        var $dashboardSel    = $widget.find('.dashboard-selection');
        var $selectProj      = $widget.find('.select-a-project');
        var $selectDash      = $widget.find('.select-a-dashboard');
        var $crfContainer    = $widget.find('.crf-container');
        var $legacyContainer = $widget.find('.crf-legacy-container');
        var $loader          = $widget.find('.home-project-dashboard-loader');
        var $resultsPanel    = $widget.find('.home-project-dashboard-results-panel');
        var $nonCrfsPanel    = $widget.find('.home-dashboard-non-crfs');
        var $openProjectLink = $widget.find('.home-project-open-link');
        var $gaugeSection    = $widget.find('.home-project-dashboard-benchmark');
        var $chartsRow       = $widget.find('.home-project-dashboard-charts-row');
        var $dashboardsWrap  = $widget.find('.home-dashboards-wrapper');
        var $overlay         = $widget.find('.incomplete-project-overlay');

        function ensureChartsAreaVisible() {
            $gaugeSection.removeClass('d-none');
            $chartsRow.removeClass('d-none');
        }

        function setProjectDashboardLoading(loading) {
            if (loading) {
                if ($resultsPanel.length) {
                    $resultsPanel.addClass('home-project-dashboard-results-loading').attr('aria-busy', 'true');
                }
                $loader.removeClass('d-none');
                return;
            }
            if ($resultsPanel.length) {
                $resultsPanel.removeClass('home-project-dashboard-results-loading').attr('aria-busy', 'false');
            }
            $loader.addClass('d-none');
        }

        function setBenchmarkUnavailableMessage(projectUrl) {
            if (!$overlay.length) {
                return;
            }
            var html =
                '<p class="overlay-message">Benchmark data for this framework is available on the project dashboard.</p>';
            if (projectUrl) {
                html += '<a href="' + projectUrl + '" class="btn btn-primary btn-sm">Open Assessment</a>';
            }
            $overlay.find('.overlay-content').html(html);
            $overlay.show();
            $widget.find('.gauge-content').addClass('position-relative');
        }

        function syncBenchmarkOverlayFromProject() {
            if (!$overlay.length) {
                return;
            }
            var $opt = $projectSel.find('option:selected').first();
            var projectId = parseInt($opt.attr('data-post-id'), 10);
            var slug = String($projectSel.val() || '').trim();
            var projectUrl = $opt.attr('data-project-url') || '';

            if (!slug) {
                $overlay.attr('data-project-id', '');
                $overlay.find('.overlay-content').html(
                    '<p class="overlay-message">Select a project above to view benchmark data.</p>'
                );
                $overlay.show();
                $widget.find('.gauge-content').addClass('position-relative');
                return;
            }

            if ($nonCrfsPanel.is(':visible')) {
                setBenchmarkUnavailableMessage(projectUrl);
                return;
            }

            if (!projectId) {
                return;
            }

            $overlay.attr('data-project-id', projectId);
            // get-project-percentage.js copies these onto the Finalize Data button, so
            // mark-complete-overlay.js can hand the project's own (native) framework id /
            // slug to updateProjectCompletedField(). Nothing else on /home sets
            // WebAssessmentCharts.frameworkId, and the server needs it.
            $overlay.attr('data-project-slug', slug);
            $overlay.attr('data-framework-slug', ($opt.attr('data-framework-slug') || '').toLowerCase());
            $overlay.attr('data-framework-id', $opt.attr('data-framework-id') || '');
            $widget.find('.gauge-content').addClass('position-relative');

            if (typeof window.updateIncompleteProjectOverlay === 'function') {
                window.updateIncompleteProjectOverlay(projectId);
            }
        }

        window.syncHomeBenchmarkGaugeOverlay = syncBenchmarkOverlayFromProject;

        // Initial state: hide panels until a project is picked; charts row stays
        // visible when projects exist (markup omits d-none) so domain chart can render.
        hideHomeProjectDashboardPlaceholders($widget);
        $crfContainer.hide();
        $legacyContainer.hide();
        $nonCrfsPanel.hide();
        $openProjectLink.hide();

        // Snapshot bundle wire-up on window.load so we can confirm the
        // dispatcher actually published its API.
        $(window).on('load.homeDashboard', function () {
            log('Dispatcher API present?', {
                homeDashboardFetchForProject:        typeof (window.WebAssessmentCharts && WebAssessmentCharts.homeDashboardFetchForProject),
                homeDashboardRenderTechnicalChart:   typeof (window.WebAssessmentCharts && WebAssessmentCharts.homeDashboardRenderTechnicalChart),
                homeDashboardActivateFrameworkPanels:typeof (window.WebAssessmentCharts && WebAssessmentCharts.homeDashboardActivateFrameworkPanels),
                homeDashboardHasFetcher:             typeof (window.WebAssessmentCharts && WebAssessmentCharts.homeDashboardHasFetcher),
                createChartFromTable:                typeof (window.WebAssessmentCharts && WebAssessmentCharts.createChartFromTable),
                fetchDataAndCalculateScores2021:     typeof (window.WebAssessmentCharts && WebAssessmentCharts.fetchDataAndCalculateScores2021),
                fetchDataAndCalculateScores2026:     typeof (window.WebAssessmentCharts && WebAssessmentCharts.fetchDataAndCalculateScores2026)
            });
        });

        // Monotonically increasing token. Every runDispatch grabs the next
        // value; when its Promise resolves it checks whether the current
        // token still equals its own — if not, a newer dispatch has started
        // (e.g. user picked a project, then quickly changed the dashboard)
        // and this stale result should NOT render over the newer one.
        // Without this guard, the user picks a 2023 project (kicks a 2023
        // fetch), then changes the dashboard to 2025 (kicks a 2025 fetch);
        // whichever request the network returns LAST wins, and that's
        // routinely the 2023 one because its quality JSON is cached locally
        // while 2025 still needs to be downloaded.
        var dispatchToken = 0;

        // Latest runDispatch() for the selected project: { slug, promise }. The
        // promise settles (never rejects) once that project's scores are loaded.
        var lastDispatch = null;

        // Finalize Data score hook. mark-complete-overlay.js calls
        // WebAssessmentCharts.fetchDataAndCalculateScoresDispatcher(projectSlug, fw)
        // and then scores the framework from what it loaded (MaturityScoresOverall<year>,
        // processedItems, ...). /web-assessment-tool gets it from dashboard-bundle
        // (framework-dispatchers.js); /home has none, so the overlay revealed the
        // button with no score and the server rejected the finalize. Here the widget
        // has already fetched the selected project's native framework, so just wait
        // for that fetch rather than re-running it (which would repaint the widget).
        // CRF-S only: the overlay scores those from WebAssessmentCharts.MaturityScoresOverall<year>,
        // which the native fetch sets. Third-party home fetchers keep their items local, so
        // the overlay's processedItems-based scorers would read another project's data; for
        // them we reject and updateProjectCompletedField() falls back to the
        // overall<Framework>maturity value the home chart module computed.
        if (window.WebAssessmentCharts &&
            typeof WebAssessmentCharts.fetchDataAndCalculateScoresDispatcher !== 'function') {
            WebAssessmentCharts.fetchDataAndCalculateScoresDispatcher = function (projectSlug, frameworkSlug) {
                if (!isCrfsFrameworkSlug(frameworkSlug)) {
                    return Promise.reject(new Error('No home Finalize score calculation for ' + frameworkSlug));
                }
                if (lastDispatch && lastDispatch.slug === String(projectSlug || '').trim()) {
                    return lastDispatch.promise;
                }
                return Promise.reject(new Error('Home dashboard has not loaded project ' + projectSlug));
            };
        }

        function runDispatch(slug, nativeFrameworkSlug, viewFrameworkSlug, primaryStandardIdForSync) {
            var nativeFw = String(nativeFrameworkSlug || '').toLowerCase();
            var viewFw = (viewFrameworkSlug != null && viewFrameworkSlug !== '')
                ? String(viewFrameworkSlug).toLowerCase()
                : nativeFw;

            var token = ++dispatchToken;

            setProjectDashboardLoading(true);
            hideHomeProjectDashboardPlaceholders($widget);
            $crfContainer.hide();
            $legacyContainer.hide();
            $widget.find('.home-dash-fw-panel').hide();

            // Sync dashboard dropdown before fetch — year scripts in the bundle show
            // ".select-a-dashboard" while option text is still "Select Dashboard".
            if (primaryStandardIdForSync) {
                updateDashboardSelectionDisplay($widget, primaryStandardIdForSync);
            }

            if (!window.WebAssessmentCharts || typeof WebAssessmentCharts.homeDashboardFetchForProject !== 'function') {
                warn('home-dashboard-bundle dispatcher is not loaded.');
                setProjectDashboardLoading(false);
                if (token === dispatchToken) {
                    markHomeDashboardProjectReady();
                }
                return;
            }

            // A fetch that fails part-way must not leave the previous project's CRF-S
            // scores behind for Finalize Data to submit under this project.
            Object.keys(WebAssessmentCharts).forEach(function (key) {
                if (/^MaturityScoresOverall\d{4}$/.test(key)) {
                    WebAssessmentCharts[key] = null;
                }
            });

            var dispatchPromise = WebAssessmentCharts.homeDashboardFetchForProject(slug, nativeFw, viewFw)
                .then(function (result) {
                    if (token !== dispatchToken) {
                        log('Stale dispatch (token=' + token + ', current=' + dispatchToken +
                            ') — skipping render.');
                        return;
                    }
                    $nonCrfsPanel.hide();
                    $openProjectLink.hide();
                    ensureChartsAreaVisible();
                    if (result && result.kind === 'framework') {
                        log('Fetch complete — activating framework panel', result.frameworkSlug || '(none)');
                        $dashboardsWrap.show();
                        if (typeof WebAssessmentCharts.homeDashboardActivateFrameworkPanels === 'function') {
                            WebAssessmentCharts.homeDashboardActivateFrameworkPanels(result.frameworkSlug);
                        }
                    } else if (result && result.kind === 'crfs') {
                        log('Fetch complete for year', result.year,
                            '— rendering CRF chart', result.frameworkSlug || '(none)');
                        WebAssessmentCharts.homeDashboardRenderTechnicalChart(
                            result.year,
                            result.frameworkSlug
                        );
                    }
                    if (primaryStandardIdForSync) {
                        updateDashboardSelectionDisplay($widget, primaryStandardIdForSync);
                    }
                })
                .catch(function (err) {
                    warn('Dispatcher failed:', err);
                    var msg = err && err.message ? String(err.message) : '';
                    var blocked = /Unsupported|no home fetcher|no home dashboard fetcher|cannot be compared|third-party frameworks/i.test(msg);
                    if (token === dispatchToken && blocked) {
                        $nonCrfsPanel.show();
                        ensureChartsAreaVisible();
                        $dashboardsWrap.hide();
                        $crfContainer.hide();
                        $legacyContainer.hide();
                        $widget.find('.home-dash-fw-panel').hide();
                        var $selProj = $projectSel.find('option:selected').first();
                        var openUrl = $selProj.attr('data-project-url');
                        if (openUrl) {
                            $openProjectLink.attr('href', openUrl).show();
                        }
                    }
                })
                .then(function () {
                    if (token === dispatchToken) {
                        setProjectDashboardLoading(false);
                        hideHomeProjectDashboardPlaceholders($widget);
                        if (primaryStandardIdForSync) {
                            updateDashboardSelectionDisplay($widget, primaryStandardIdForSync);
                        }
                        if (typeof window.syncHomeBenchmarkGaugeOverlay === 'function') {
                            window.syncHomeBenchmarkGaugeOverlay();
                        }
                        markHomeDashboardProjectReady();
                    }
                });
            lastDispatch = { slug: String(slug || '').trim(), promise: dispatchPromise };
        }

        /**
         * First accessible project is selected in markup when the list is non-empty.
         * Fire change so charts load (loading the page does not trigger change).
         * No real project options (e.g. new user): leave placeholder / hints only.
         */
        function triggerInitialProjectLoadIfAvailable() {
            var $realProjectOpts = $projectSel.find('option').filter(function () {
                return !this.disabled && String(this.value || '').trim() !== '';
            });
            if (!$realProjectOpts.length) {
                log('No projects to auto-select.');
                syncHomeDashboardSelectEnabledState($widget);
                markHomeDashboardProjectReady();
                return;
            }
            var firstSlug = $realProjectOpts.first().attr('value');
            if (String($projectSel.val() || '').trim() !== String(firstSlug).trim()) {
                $projectSel.val(firstSlug);
            }
            $projectSel.trigger('change');
        }

        $projectSel.on('change', function () {
            var slug = $(this).val();
            var slugStr = String(slug || '').trim();
            var $opt = $(this).find('option:selected').first();
            var frameworkSlug = ($opt.attr('data-framework-slug') || '').toLowerCase();
            var primaryStandardId = $opt.attr('data-primary-standardid') || '';

            log('Project selected:', slug,
                '— framework:', frameworkSlug || '(none)',
                '— primaryStandardId:', primaryStandardId || '(none)');

            if (!slugStr) {
                dispatchToken++;
                setProjectDashboardLoading(false);
                $selectProj.show();
                $selectDash.hide();
                $crfContainer.hide();
                $legacyContainer.hide();
                $nonCrfsPanel.hide();
                $openProjectLink.hide();
                $gaugeSection.addClass('d-none');
                $chartsRow.addClass('d-none');
                $widget.find('.home-dash-fw-panel').hide();
                syncHomeDashboardSelectEnabledState($widget);
                syncBenchmarkOverlayFromProject();
                return;
            }

            hideHomeProjectDashboardPlaceholders($widget);
            ensureChartsAreaVisible();

            syncHomeDashboardSelectEnabledState($widget);
            syncBenchmarkOverlayFromProject();
            window.setTimeout(syncBenchmarkOverlayFromProject, 2500);

            if (isCrfsFrameworkSlug(frameworkSlug)) {
                $nonCrfsPanel.hide();
                $openProjectLink.hide();
                $dashboardsWrap.show();
                runDispatch(slug, frameworkSlug, frameworkSlug, primaryStandardId);
                return;
            }

            if (homeDashboardHasRegisteredFetcher(frameworkSlug)) {
                $nonCrfsPanel.hide();
                $openProjectLink.hide();
                $dashboardsWrap.show();
                runDispatch(slug, frameworkSlug, frameworkSlug, primaryStandardId);
                return;
            }

            dispatchToken++;
            setProjectDashboardLoading(false);
            $crfContainer.hide();
            $legacyContainer.hide();
            $dashboardsWrap.hide();
            $widget.find('.home-dash-fw-panel').hide();
            $nonCrfsPanel.show();
            var permalink = $opt.attr('data-project-url');
            if (permalink) {
                $openProjectLink.attr('href', permalink).show();
            } else {
                $openProjectLink.hide();
            }
            log('Project framework has no home-dashboard fetcher —', frameworkSlug || '(none)');
            markHomeDashboardProjectReady();
        });

        $dashboardSel.on('change', function () {
            if (suppressDashboardChange) {
                log('Dashboard change suppressed (programmatic sync from project pick).');
                return;
            }
            if ($dashboardSel.prop('disabled')) {
                log('Dashboard change ignored — selection is locked to the project.');
                return;
            }
            var projectSlug = $projectSel.val();
            if (!projectSlug) {
                $selectDash.show();
                return;
            }
            var $projOpt = $projectSel.find('option:selected').first();
            var nativeFrameworkSlug = ($projOpt.attr('data-framework-slug') || '').toLowerCase();
            var $opt = $(this).find('option:selected').first();
            var viewSlug = dashboardOptionToFrameworkSlug($opt);
            if (!viewSlug) {
                warn('Could not derive framework slug from dashboard option', $opt.attr('data-standardid'));
                return;
            }
            if (!isCrfsFrameworkSlug(nativeFrameworkSlug)) {
                if (nativeFrameworkSlug !== viewSlug) {
                    warn('Home dashboard only scores third-party projects against their own standard — pick the row that matches this project.');
                    return;
                }
                if (!homeDashboardHasRegisteredFetcher(nativeFrameworkSlug)) {
                    log('Dashboard change ignored — project framework has no home fetcher.');
                    return;
                }
            }
            log('Dashboard manually changed to', $opt.text(), '→ view slug', viewSlug,
                '(project native', nativeFrameworkSlug || '(none)', ')');
            $selectDash.hide();
            runDispatch(projectSlug, nativeFrameworkSlug, viewSlug, null);
        });

        triggerInitialProjectLoadIfAvailable();
        window.setTimeout(syncBenchmarkOverlayFromProject, 600);
    }

    $(function () {
        log('Initializing home dashboard widgets…');

        if ($('.home-dashboard-widgets').length) {
            window.setTimeout(function () {
                if ($('#main-content').hasClass('d-none')) {
                    warn('Home page loader timeout — revealing content.');
                    homeDashboardReadyState.charts = true;
                    homeDashboardReadyState.projectDashboard = true;
                    tryRevealHomeDashboardWidgets();
                }
            }, HOME_DASHBOARD_LOADER_MAX_MS);
        }

        // Risk + asset charts paint immediately — they don't depend on the
        // bundle's dispatcher and don't share any classes with it.
        renderRiskChart();
        renderRiskBusinessUnitChart();
        renderAssetChart();
        markHomeDashboardChartsReady();
        // initProjectDashboardWidget attaches change handlers on
        // .project-selection / .dashboard-selection. The bundle's dispatcher
        // (home-dashboard-dispatch.js) detaches ALL change handlers on those
        // selects via setTimeout(0) from its own $(document).ready — without
        // its own setTimeout it can't see the year files' handlers because
        // those are attached in $(document).ready callbacks too. We have to
        // queue our binding behind the dispatcher's detach so it doesn't
        // also strip ours. Both are setTimeout(0); ours runs second because
        // home-dashboard.js's ready callback is registered after the bundle.
        window.setTimeout(initProjectDashboardWidget, 0);
    });
})(jQuery);
